/**
 * Locale-aware number / date formatting. Replaces the hardcoded
 * `toLocaleString("he-IL")` calls that used to be scattered over the pages.
 *
 * The active language is pushed in by `lib/i18n.ts` on every language change
 * so plain (non-React) modules can format without a hook. React components
 * re-render on language change through `useTranslation`, which makes these
 * helpers safe to call inside render.
 */
import { DEFAULT_LANGUAGE, languageMeta } from "./languages";

let activeLocale = languageMeta(DEFAULT_LANGUAGE).intlLocale;

export function setFormatLanguage(code: string): void {
  activeLocale = languageMeta(code).intlLocale;
}

export function currentLocale(): string {
  return activeLocale;
}

type DateInput = Date | string | number | null | undefined;

function toDate(v: DateInput): Date | null {
  if (v === null || v === undefined || v === "") return null;
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function fmtNum(n: number | null | undefined, opts?: Intl.NumberFormatOptions): string {
  if (n === null || n === undefined || Number.isNaN(n)) return "";
  return new Intl.NumberFormat(activeLocale, opts).format(n);
}

/**
 * A duration given in minutes, in the reader's language with short units:
 * minutes below 90, hours from there ("45 min", "1.5 hr", "20 hr" in
 * English). Intl's unit style, so there is no counted-noun string (and no
 * plural rule) to keep in 20 bundles. One decimal under 10, whole numbers
 * above.
 */
export function fmtDuration(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined || !Number.isFinite(minutes)) return "";
  const m = Math.max(0, minutes);
  const inHours = m >= 90;
  const n = inHours ? m / 60 : m;
  return new Intl.NumberFormat(activeLocale, {
    style: "unit",
    unit: inHours ? "hour" : "minute",
    unitDisplay: "short",
    maximumFractionDigits: n < 10 ? 1 : 0,
  }).format(n);
}

/** 0..1 ratio → "87%" */
export function fmtPercent(ratio: number | null | undefined, digits = 0): string {
  if (ratio === null || ratio === undefined || Number.isNaN(ratio)) return "";
  return new Intl.NumberFormat(activeLocale, {
    style: "percent",
    maximumFractionDigits: digits,
  }).format(ratio);
}

export function fmtDate(v: DateInput, opts?: Intl.DateTimeFormatOptions): string {
  const d = toDate(v);
  if (!d) return "";
  return new Intl.DateTimeFormat(activeLocale, opts ?? { year: "numeric", month: "2-digit", day: "2-digit" }).format(d);
}

export function fmtTime(v: DateInput, opts?: Intl.DateTimeFormatOptions): string {
  const d = toDate(v);
  if (!d) return "";
  return new Intl.DateTimeFormat(activeLocale, opts ?? { hour: "2-digit", minute: "2-digit" }).format(d);
}

export function fmtTimestamp(v: DateInput, opts?: Intl.DateTimeFormatOptions): string {
  const d = toDate(v);
  if (!d) return "";
  return new Intl.DateTimeFormat(
    activeLocale,
    opts ?? { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" },
  ).format(d);
}

/** Weekday name for a 0..6 index (0 = Sunday) in the active locale. */
export function fmtWeekday(index: number, width: "long" | "short" | "narrow" = "short"): string {
  // 2023-01-01 was a Sunday.
  const d = new Date(Date.UTC(2023, 0, 1 + ((index % 7) + 7) % 7));
  return new Intl.DateTimeFormat(activeLocale, { weekday: width, timeZone: "UTC" }).format(d);
}

/**
 * "6 minutes ago" / "yesterday" in the active locale (Intl.RelativeTimeFormat,
 * so every one of the 20 languages gets its own grammar and plural forms —
 * never a hand-built "{{n}} minutes ago" string). Under a minute reads as
 * "now". Beyond a week it falls back to the date.
 */
export function fmtRelative(v: DateInput, now: number = Date.now()): string {
  const d = toDate(v);
  if (!d) return "";
  const sec = Math.round((d.getTime() - now) / 1000);
  const abs = Math.abs(sec);
  // "long", not "short": the short forms are abbreviations ("6 min. ago",
  // and the Hebrew equivalent with a geresh) that read as clipped in a list.
  const rtf = new Intl.RelativeTimeFormat(activeLocale, { numeric: "auto", style: "long" });
  // ICU's Hebrew one/two forms carry the count again in parentheses
  // ("an hour ago (1)", "two hours ago (2)") although the word already says it.
  // No locale otherwise ends a relative time in "(n)", so strip it for all.
  const clean = (s: string) => s.replace(/\s*\(\d+\)$/, "");
  if (abs < 60) return clean(rtf.format(0, "second"));
  if (abs < 3600) return clean(rtf.format(Math.round(sec / 60), "minute"));
  if (abs < 86400) return clean(rtf.format(Math.round(sec / 3600), "hour"));
  if (abs < 7 * 86400) return clean(rtf.format(Math.round(sec / 86400), "day"));
  return fmtDate(d);
}
